import {
  AppBar,
  Box,
  Button,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useTheme,
  Breadcrumbs,
  Link,
  Menu,
  MenuItem,
  Card,
} from "@mui/material";
import React, { Suspense, useCallback, useEffect, useState } from "react";
import { DashboardSkeleton, TableSkeleton, CardSkeleton } from "./SkeletonLoader";

import LogoutIcon from "@mui/icons-material/LogoutOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import BusinessIcon from "@mui/icons-material/Business";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";

import { MixTheme } from "@styles";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ROLES } from "src/constants/roles";
import { RouterPath } from "src/constants/RouterPath";
import { logout } from "src/redux/user/actions";
import StorageService from "src/storage";

import GalleryIcon from "@mui/icons-material/BrowseGalleryOutlined";
import AccidentIcon from "@mui/icons-material/ErrorOutlineOutlined";
import HomeIcon from "@mui/icons-material/Home";
import LocalDiningIcon from "@mui/icons-material/LocalDining";
import ManagerIcon from "@mui/icons-material/ManageAccounts";
import NotificationIcon from "@mui/icons-material/NotificationAddOutlined";
import TrainingsIcon from "@mui/icons-material/OnDeviceTrainingOutlined";
import QuestionsIcon from "@mui/icons-material/QuestionMarkOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import StoreIcon from "@mui/icons-material/Storefront";
import LockIcon from "@mui/icons-material/LockOutlined";
import NotificationBell from "./NotificationBell";


interface DrawerItems {
  title: string;
  icon: JSX.Element;
  to: string;
  subPages?: string[];
  onlyAdmin: boolean;
  isLogout?: boolean;
  category: "Operations" | "Compliance" | "Engagement" | "Administration";
}

const Drawers: DrawerItems[] = [
  { title: "Home", icon: <HomeIcon />, to: "/", onlyAdmin: false, category: "Operations" },
  { title: "Stores", icon: <StoreIcon />, to: "/stores", onlyAdmin: false, category: "Operations" },
  { title: "Lead", icon: <ManagerIcon />, to: "/lead", onlyAdmin: true, category: "Operations" },
  { title: "Customise Tasks", icon: <QuestionsIcon />, to: "/questions", onlyAdmin: true, category: "Compliance" },
  { title: "A & I Reports", icon: <AccidentIcon />, to: "/accident", onlyAdmin: false, category: "Compliance" },
  { title: "Gallery", icon: <GalleryIcon />, to: "/gallery", onlyAdmin: false, category: "Compliance" },
  { title: "Communications", icon: <NotificationIcon />, to: "/notifications", onlyAdmin: true, category: "Engagement" },
  { title: "New Promotions", icon: <LocalDiningIcon />, to: "/promotions", onlyAdmin: false, category: "Engagement" },
  { title: "Other Trainings", icon: <TrainingsIcon />, to: "/trainings", onlyAdmin: true, category: "Engagement" },
  { title: "Settings", icon: <SettingsIcon />, to: "/settings", onlyAdmin: false, category: "Administration" },
];

const PageSkeletonLoader = () => {
  const location = useLocation();
  const path = location.pathname;

  if (path === "/" || path === "/dashboard" || path === "/reports") {
    return <DashboardSkeleton />;
  }

  const isTablePage =
    path.startsWith("/stores") ||
    path.startsWith("/lead") ||
    path.startsWith("/questions") ||
    path.startsWith("/accident") ||
    path.startsWith("/gallery") ||
    path.startsWith("/trainings") ||
    path.startsWith("/promotions") ||
    path.startsWith("/notifications");

  if (isTablePage) {
    return (
      <Box p={1}>
        <TableSkeleton columns={5} rows={6} />
      </Box>
    );
  }

  return <CardSkeleton />;
};

const isSidebarItemVisible = (title: string, role: string) => {
  if (role === ROLES.ADMIN || role === ROLES.APPADMIN) {
    return true;
  }
  if (role === ROLES.AREAMANAGER) {
    const allowed = ["Home", "Stores", "A & I Reports"];
    return allowed.includes(title);
  }
  return ["Home", "A & I Reports"].includes(title);
};

const DrawerLayout = () => {
  const theme = useTheme<MixTheme>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.user.user);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => localStorage.getItem("sidebar_collapsed") === "true");
  const [workspaceAnchorEl, setWorkspaceAnchorEl] = useState<null | HTMLElement>(null);

  const toggleCollapse = useCallback(() => {
    setIsCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem("sidebar_collapsed", next ? "true" : "false");
      return next;
    });
  }, []);

  const handleWorkspaceClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setWorkspaceAnchorEl(event.currentTarget);
  }, []);

  const handleWorkspaceClose = useCallback(() => {
    setWorkspaceAnchorEl(null);
  }, []);

  const currentDrawerWidth = isCollapsed ? 72 : 260;

  const activePath = location.pathname;

  const isActiveRoute = useCallback(
    (route: string) =>
      route === "/" ? activePath === "/" : activePath.startsWith(route),
    [activePath]
  );



  const confirmLogout = useCallback(async () => {
    setLogoutOpen(false);
    StorageService.token.clearAll();
    await dispatch(logout());
    navigate("/");
  }, [dispatch, navigate]);

  /**
   * TRIAL PROTECTION LOGIC
   * Redirect users to Home if they try to access unauthorized routes during trial
   */
  useEffect(() => {
    if (user?.status === "TRIAL_LIMITED") {
      const allowedPaths = ["/", "/dashboard", "/reports", "/reports/details", RouterPath.setPassword, "/settings"];
      const isAllowed =
        allowedPaths.includes(location.pathname) ||
        location.pathname.startsWith("/stores");

      if (!isAllowed) {
        navigate("/", { replace: true });
      }
    }
  }, [user, location.pathname, navigate]);

  /**
   * ONBOARDING PASSWORD CHANGE ENFORCEMENT
   */
  useEffect(() => {
    if (user?.is_password_change === 0 || user?.is_password_change === false) {
      if (location.pathname !== RouterPath.setPassword) {
        navigate(RouterPath.setPassword, { replace: true });
      }
    }
  }, [user, location.pathname, navigate]);



  /**
   * SET PASSWORD PAGE → TOP BAR ONLY
   */
  if (location.pathname === RouterPath.setPassword) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#F8FAFC" }}>
        <CssBaseline />
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            backgroundColor: "#fff",
            borderBottom: "1px solid #E2E8F0",
          }}
        >
          <Toolbar sx={{ px: 3, display: "flex", alignItems: "center" }}>
            <Box display="flex" alignItems="center" gap={1.25}>
              <img src="/logo.png" alt="Tyfhu logo" style={{ width: 28, height: 28 }} />
              <Typography fontSize="20px" fontWeight={800} color="primary.main" sx={{ letterSpacing: "-0.5px" }}>
                Tyfhu
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ mx: 2, my: 1.5 }} />
            <Typography fontSize="14px" fontWeight={600} color="text.secondary">
              Set Password
            </Typography>
          </Toolbar>
        </AppBar>
        <Box pt="64px" sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
          <Suspense fallback={<PageSkeletonLoader />}>
            <Outlet />
          </Suspense>
        </Box>
      </Box>
    );
  }

  /**
   * DRAWER CONTENT (MATCHES AdminDrawer)
   */
  const drawerContent = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: "#F8FAFC" }}>
      {/* Brand */}
      <Box sx={{ px: isCollapsed ? 2 : 3, py: 2.5, display: "flex", flexDirection: "column", alignItems: isCollapsed ? "center" : "flex-start" }}>
        <Box display="flex" alignItems="center" gap={1.25}>
          <img src="/logo.png" alt="Tyfhu logo" style={{ width: 28, height: 28 }} />
          {!isCollapsed && (
            <Typography fontSize="20px" fontWeight={800} color="primary.main" sx={{ letterSpacing: "-0.5px" }}>
              Tyfhu
            </Typography>
          )}
        </Box>
        {!isCollapsed && (
          <Typography fontSize="11px" color="text.secondary" noWrap sx={{ maxWidth: 200, mt: 0.5 }}>
            {user?.company_name}
          </Typography>
        )}
      </Box>

      <Divider />

      {/* Navigation */}
      <Box sx={{ px: 1, py: 1, flexGrow: 1, overflowY: "auto" }}>
        {(["Operations", "Compliance", "Engagement", "Administration"] as const).map((cat) => {
          const catItems = Drawers.filter(
            (item) => item.category === cat && isSidebarItemVisible(item.title, user?.role)
          );

          if (catItems.length === 0) return null;

          return (
            <Box key={cat} sx={{ mb: 1.5 }}>
              {!isCollapsed && (
                <Typography
                  fontSize="10px"
                  fontWeight={700}
                  color="text.disabled"
                  sx={{
                    px: 2,
                    py: 0.5,
                    mb: 0.5,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  {cat}
                </Typography>
              )}
              <List disablePadding>
                {catItems.map((item) => {
                  // Feature Gating Logic
                  const isTrialLimited = user?.status === "TRIAL_LIMITED";
                  const allowedInTrial = ["Home", "Stores", "Settings"];
                  const isLocked = isTrialLimited && !allowedInTrial.includes(item.title);

                  const active = isActiveRoute(item.to);

                  return (
                    <ListItem key={item.title} disablePadding sx={{ mb: "4px" }}>
                      <ListItemButton
                        disabled={isLocked}
                        onClick={() => {
                          if (isLocked) return;
                          navigate(item.to);
                          setMobileOpen(false);
                        }}
                        sx={{
                          borderRadius: "10px",
                          px: isCollapsed ? 1.5 : 2,
                          py: 1,
                          justifyContent: isCollapsed ? "center" : "flex-start",
                          opacity: isLocked ? 0.6 : 1,
                          backgroundColor: active
                            ? theme.palette.primary.main + "14"
                            : "transparent",
                          "&:hover": {
                            backgroundColor: isLocked
                              ? "transparent"
                              : theme.palette.primary.main + "0F",
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: isCollapsed ? 0 : 36,
                            justifyContent: "center",
                            color: isLocked
                              ? theme.palette.text.disabled
                              : active
                                ? theme.palette.primary.main
                                : theme.palette.text.secondary,
                          }}
                        >
                          {isLocked ? <LockIcon fontSize="small" /> : item.icon}
                        </ListItemIcon>
                        {!isCollapsed && (
                          <ListItemText
                            primary={item.title}
                            primaryTypographyProps={{
                              fontSize: "13px",
                              fontWeight: active ? 600 : 500,
                              color: isLocked ? "text.disabled" : "inherit"
                            }}
                          />
                        )}
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          );
        })}
      </Box>

      {/* Collapse Toggle */}
      <Box sx={{ px: 1, pb: 1, display: { xs: "none", sm: "block" } }}>
        <ListItemButton
          onClick={toggleCollapse}
          sx={{
            borderRadius: "10px",
            px: isCollapsed ? 1.5 : 2,
            justifyContent: isCollapsed ? "center" : "flex-start",
            color: "text.secondary",
          }}
        >
          <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 36, justifyContent: "center", color: "inherit" }}>
            {isCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
          </ListItemIcon>
          {!isCollapsed && (
            <ListItemText
              primary="Collapse"
              primaryTypographyProps={{ fontSize: "13px", fontWeight: 500 }}
            />
          )}
        </ListItemButton>
      </Box>

      {/* Logout */}
      <Box sx={{ px: 1, pb: 2 }}>
        <ListItemButton
          onClick={() => setLogoutOpen(true)}
          sx={{
            borderRadius: "10px",
            px: isCollapsed ? 1.5 : 2,
            justifyContent: isCollapsed ? "center" : "flex-start",
            color: theme.palette.error.main,
            "&:hover": {
              backgroundColor: theme.palette.error.main + "10",
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 36, justifyContent: "center", color: "inherit" }}>
            <LogoutIcon />
          </ListItemIcon>
          {!isCollapsed && (
            <ListItemText
              primary="Logout"
              primaryTypographyProps={{ fontSize: "13px", fontWeight: 500 }}
            />
          )}
        </ListItemButton>
      </Box>
    </Box>
  );

  const pathnames = location.pathname.split("/").filter((x) => x);

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      {/* Top Bar */}
      <AppBar
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { sm: `${currentDrawerWidth}px` },
          backgroundColor: "#fff",
          borderBottom: "1px solid #E2E8F0",
          transition: (t) => t.transitions.create(['width', 'margin'], {
            easing: t.transitions.easing.sharp,
            duration: t.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton
              onClick={() => setMobileOpen(!mobileOpen)}
              sx={{ display: { sm: "none" }, mr: 1 }}
            >
              <MenuIcon />
            </IconButton>

            {/* Breadcrumbs */}
            <Breadcrumbs separator="/" sx={{ display: { xs: "none", sm: "block" }, color: "text.disabled" }}>
              <Link component={RouterLink} to="/" underline="hover" color="text.secondary" fontSize={13} fontWeight={500}>
                Home
              </Link>
              {pathnames.map((name, index) => {
                const isLast = index === pathnames.length - 1;
                const to = `/${pathnames.slice(0, index + 1).join("/")}`;

                let displayName = name.charAt(0).toUpperCase() + name.slice(1);
                if (name === "lead") displayName = "Leads";
                if (name === "accident") displayName = "A & I Reports";
                if (name === "questions") displayName = "Customise Tasks";

                return isLast ? (
                  <Typography key={to} color="text.primary" fontSize={13} fontWeight={600}>
                    {displayName}
                  </Typography>
                ) : (
                  <Link component={RouterLink} to={to} underline="hover" color="text.secondary" key={to} fontSize={13} fontWeight={500}>
                    {displayName}
                  </Link>
                );
              })}
            </Breadcrumbs>
          </Box>

          <Box display="flex" alignItems="center" gap={2}>
            {/* Workspace Switcher */}
            <Button
              size="small"
              onClick={handleWorkspaceClick}
              startIcon={<BusinessIcon fontSize="small" />}
              endIcon={<UnfoldMoreIcon fontSize="small" />}
              sx={{
                color: "text.primary",
                fontWeight: 600,
                fontSize: "13px",
                px: 1.5,
                py: 0.5,
                borderRadius: "8px",
                border: "1px solid #E2E8F0",
                textTransform: "none",
                display: { xs: "none", md: "flex" },
                "&:hover": {
                  borderColor: "primary.main",
                  bgcolor: "transparent",
                }
              }}
            >
              {user?.company_name || "Tyfhu Workspace"}
            </Button>
            <Menu
              anchorEl={workspaceAnchorEl}
              open={Boolean(workspaceAnchorEl)}
              onClose={handleWorkspaceClose}
              PaperProps={{
                sx: {
                  borderRadius: "12px",
                  mt: 0.5,
                  border: "1px solid #E2E8F0",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                  minWidth: 180,
                }
              }}
            >
              <MenuItem onClick={handleWorkspaceClose} selected sx={{ fontSize: "13px", fontWeight: 600 }}>
                {user?.company_name || "Tyfhu Workspace"}
              </MenuItem>
              <MenuItem onClick={handleWorkspaceClose} sx={{ fontSize: "13px", color: "text.secondary" }}>
                Personal Workspace
              </MenuItem>
            </Menu>

            {/* Trial Banner */}
            {user?.status === "TRIAL_LIMITED" && (
              <Box
                sx={{
                  backgroundColor: "primary.main",
                  color: "white",
                  px: 2,
                  py: 0.5,
                  borderRadius: "20px",
                  display: "flex",
                  alignItems: "center",
                  gap: 1
                }}
              >
                <Typography fontSize="12px" fontWeight={600}>
                  Trial Version
                </Typography>
                <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.3)" }} />
                <Typography fontSize="11px">
                  Contact Tyfhu Admin to unlock all features
                </Typography>
              </Box>
            )}

            {/* Notification Bell */}
            <NotificationBell />
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: currentDrawerWidth,
          display: { xs: "none", sm: "block" },
          flexShrink: 0,
          whiteSpace: 'nowrap',
          transition: (t) => t.transitions.create('width', {
            easing: t.transitions.easing.sharp,
            duration: t.transitions.duration.enteringScreen,
          }),
          "& .MuiDrawer-paper": {
            width: currentDrawerWidth,
            backgroundColor: "#F8FAFC",
            borderRight: "1px solid #E2E8F0",
            overflowX: 'hidden',
            transition: (t) => t.transitions.create('width', {
              easing: t.transitions.easing.sharp,
              duration: t.transitions.duration.enteringScreen,
            }),
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": { width: 260 },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Main */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: "64px",
          px: 3,
          py: 3,
          backgroundColor: "#f9fafb",
          minHeight: "100vh",
          position: "relative",
        }}
      >
        {/* SaaS Global Billing Banners */}
        {user?.company_state === "TRIAL" && (
          <Box sx={{ mb: 3, p: 2, bgcolor: "#FFFBEB", border: "1px solid #FEF3C7", borderRadius: "12px", display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#92400E" }}>
              🎉 You have {user?.trial_days_remaining || 14} days remaining in your free trial. Set up AutoPay to prevent service interruption.
            </Typography>
            <Button
              variant="contained"
              size="small"
              color="warning"
              onClick={() => navigate(RouterPath.settings)}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: "6px" }}
            >
              Set Up AutoPay
            </Button>
          </Box>
        )}

        {user?.company_state === "READ_ONLY" && (
          <Box sx={{ mb: 3, p: 2, bgcolor: "#FEF2F2", border: "1px solid #FEE2E2", borderRadius: "12px", display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#991B1B" }}>
              🔒 Your free trial has expired and your account is in read-only mode. Set up AutoPay to restore complete write access.
            </Typography>
            <Button
              variant="contained"
              size="small"
              color="error"
              onClick={() => navigate(RouterPath.settings)}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: "6px" }}
            >
              Configure AutoPay
            </Button>
          </Box>
        )}

        <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              style={{ width: "100%", height: "100%" }}
            >
              <Suspense fallback={<PageSkeletonLoader />}>
                <Outlet />
              </Suspense>
            </motion.div>
          </AnimatePresence>

          {/* READ ONLY OVERLAY SCREEN BLOCKER */}
          {user?.company_state === "READ_ONLY" && !location.pathname.includes("/settings") && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                minHeight: "400px",
                backdropFilter: "blur(6px)",
                backgroundColor: "rgba(249, 250, 251, 0.7)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
                borderRadius: "16px",
              }}
            >
              <Card
                variant="outlined"
                sx={{
                  p: 5,
                  maxWidth: "480px",
                  textAlign: "center",
                  borderRadius: "20px",
                  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.05)",
                  borderColor: "rgba(0,0,0,0.06)",
                  bgcolor: "#ffffff",
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 800, color: "primary.main", mb: 2 }}>
                  Unlock Full Access
                </Typography>
                <Typography sx={{ fontSize: "14px", color: "text.secondary", mb: 4, lineHeight: 1.6 }}>
                  Your trial subscription has expired. Setting up AutoPay takes less than a minute and instantly restores write capabilities, allowing you to add audit results, checklists, and manage team credentials.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate(RouterPath.settings)}
                  sx={{ py: 1.5, px: 6, fontWeight: 800, textTransform: "none", borderRadius: "8px" }}
                >
                  Set Up AutoPay
                </Button>
              </Card>
            </Box>
          )}
        </Box>
      </Box>

      {/* Logout Confirmation */}
      <Dialog open={logoutOpen} onClose={() => setLogoutOpen(false)}>
        <DialogTitle>Logout</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to log out?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutOpen(false)}>Cancel</Button>
          <Button color="error" onClick={confirmLogout}>
            Logout
          </Button>
        </DialogActions>
      </Dialog>


    </Box>
  );
};

export default DrawerLayout;